import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

function VestingTimeline({ events, currency, exchangeRate, currentPrice }) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [showFuture, setShowFuture] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const formatCurrency = (usd) => {
    if (usd == null) return '—';
    if (currency === 'NOK' && exchangeRate) {
      return `${(usd * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }
    return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const { years, grantColors, grantYears, futureCount, pastCount } = useMemo(() => {
    const list = Array.isArray(events) ? events : [];

    const byYear = new Map();
    list.forEach((e) => {
      const year = new Date(e.vestDate).getFullYear();
      if (!byYear.has(year)) byYear.set(year, []);
      byYear.get(year).push(e);
    });

    // Newest first, both between years and inside each year.
    byYear.forEach((yearEvents) => {
      yearEvents.sort((a, b) => new Date(b.vestDate) - new Date(a.vestDate));
    });

    const colorPalette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
    const grants = [...new Set(list.map((e) => e.compYear))].sort();
    const colors = {};
    grants.forEach((y, i) => { colors[y] = colorPalette[i % colorPalette.length]; });

    return {
      years: [...byYear.entries()].sort((a, b) => b[0] - a[0]),
      grantColors: colors,
      grantYears: grants,
      futureCount: list.filter((e) => new Date(e.vestDate).getFullYear() > currentYear).length,
      pastCount: list.filter((e) => new Date(e.vestDate).getFullYear() < currentYear).length
    };
  }, [events, currentYear]);

  if (!events || events.length === 0) {
    return <div className="empty-state"><p>{t('compensation.noVestingEvents')}</p></div>;
  }

  const visibleYears = years.filter(([year]) => {
    if (year === currentYear) return true;
    return year > currentYear ? showFuture : showPast;
  });

  const now = new Date();

  return (
    <div className="vesting-timeline">
      <div className="vesting-header">
        <h3>{t('compensation.vestingTimeline')}</h3>
        <div className="vesting-range-toggles">
          {futureCount > 0 && (
            <button
              type="button"
              className={`range-toggle ${showFuture ? 'active' : ''}`}
              aria-pressed={showFuture}
              onClick={() => setShowFuture((v) => !v)}
            >
              {showFuture
                ? t('compensation.hideFutureYears')
                : t('compensation.showFutureYears', { count: futureCount })}
            </button>
          )}
          {pastCount > 0 && (
            <button
              type="button"
              className={`range-toggle ${showPast ? 'active' : ''}`}
              aria-pressed={showPast}
              onClick={() => setShowPast((v) => !v)}
            >
              {showPast
                ? t('compensation.hideEarlierYears')
                : t('compensation.showEarlierYears', { count: pastCount })}
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="vesting-legend">
        {grantYears.map((y) => (
          <span key={y} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: grantColors[y] }}></span>
            {t('compensation.grantYear')} {y}
          </span>
        ))}
      </div>

      {visibleYears.length === 0 && (
        <div className="empty-state"><p>{t('compensation.noVestingThisYear')}</p></div>
      )}

      {visibleYears.map(([year, yearEvents]) => (
        <div key={year} className="timeline-year">
          <h4 className="timeline-year-label">{year}</h4>
          <div className="timeline-events">
            {yearEvents.map((event) => {
              const vestDate = new Date(event.vestDate);
              const isPast = vestDate <= now;
              const price = event.isActual && event.stockPriceAtVest
                ? event.stockPriceAtVest
                : currentPrice;
              const value = price ? event.sharesVested * price : null;

              return (
                <div
                  key={event.id}
                  className={`timeline-event ${isPast ? 'past' : 'future'}`}
                  style={{ borderLeftColor: grantColors[event.compYear] }}
                >
                  <div className="event-date">
                    {vestDate.toLocaleDateString('nb-NO', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className="event-details">
                    <span className="event-shares">{event.sharesVested?.toFixed(2)} {t('compensation.shares')}</span>
                    {event.isSignOnBonus && <span className="event-tag sign-on">{t('compensation.signOn')}</span>}
                    <span className="event-value">{formatCurrency(value)}</span>
                    <span className="event-grant" style={{ color: grantColors[event.compYear] }}>
                      {event.compYear}
                    </span>
                  </div>
                  {event.isActual && <span className="event-badge actual">✓</span>}
                  {!event.isActual && <span className="event-badge projected">{t('compensation.projected')}</span>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default VestingTimeline;
